import * as React from 'react';
import { Users, Shield, ClipboardList, LayoutDashboard } from 'lucide-react';
import { Tabs } from '@components/base/navigation';
import { Icon, Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

function TabsShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [controlledTab, setControlledTab] = React.useState('overview');

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="tabs">
          <Tabs
            defaultValue="policy"
            variant={playgroundProps.variant as 'underline' | 'contained'}
          >
            <Tabs.List>
              <Tabs.Tab value="policy">Policy Details</Tabs.Tab>
              <Tabs.Tab value="coverage">Coverage</Tabs.Tab>
              <Tabs.Tab value="claims">Claims</Tabs.Tab>
              <Tabs.Tab value="documents">Documents</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="policy">
              <Text>View and manage your policy information, including policyholder details and effective dates.</Text>
            </Tabs.Panel>
            <Tabs.Panel value="coverage">
              <Text>Review your coverage limits, deductibles, and included protections.</Text>
            </Tabs.Panel>
            <Tabs.Panel value="claims">
              <Text>Track your claims history and submit new claims for processing.</Text>
            </Tabs.Panel>
            <Tabs.Panel value="documents">
              <Text>Access policy documents, certificates, and official correspondence.</Text>
            </Tabs.Panel>
          </Tabs>
      </ShowcasePreviewSection>

      {/* Variants */}
      <ShowcaseDemoSection
        title="Variants"
        description="Tabs support two visual styles: underline (default) and contained"
       
      >
          <VStack gap={6}>
            {(['underline', 'contained'] as const).map((variant) => (
              <div key={variant}>
                <Text size="sm" color="secondary" className="mb-2">{variant.charAt(0).toUpperCase() + variant.slice(1)} Variant</Text>
                <Tabs defaultValue="overview" variant={variant}>
                  <Tabs.List>
                    <Tabs.Tab value="overview">Overview</Tabs.Tab>
                    <Tabs.Tab value="details">Details</Tabs.Tab>
                    <Tabs.Tab value="history">History</Tabs.Tab>
                  </Tabs.List>
                  <Tabs.Panel value="overview">
                    <Text>Policy overview with key metrics and status information.</Text>
                  </Tabs.Panel>
                  <Tabs.Panel value="details">
                    <Text>Detailed policy information including all terms and conditions.</Text>
                  </Tabs.Panel>
                  <Tabs.Panel value="history">
                    <Text>Complete history of policy changes and modifications.</Text>
                  </Tabs.Panel>
                </Tabs>
              </div>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Controlled Tabs */}
      <ShowcaseDemoSection
        title="Controlled Tabs"
        description="Tabs can be controlled externally via value and onChange props"
       
      >
          <VStack gap={4}>
            <HStack gap={2}>
              <Text size="sm" color="secondary">Current tab:</Text>
              <Text size="sm" weight="medium">{controlledTab}</Text>
            </HStack>
            <Tabs value={controlledTab} onChange={setControlledTab}>
              <Tabs.List>
                <Tabs.Tab value="overview">Overview</Tabs.Tab>
                <Tabs.Tab value="underwriting">Underwriting</Tabs.Tab>
                <Tabs.Tab value="billing">Billing</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="overview">
                <Text>Policy overview and summary information.</Text>
              </Tabs.Panel>
              <Tabs.Panel value="underwriting">
                <Text>Underwriting details and risk assessment data.</Text>
              </Tabs.Panel>
              <Tabs.Panel value="billing">
                <Text>Billing information and payment history.</Text>
              </Tabs.Panel>
            </Tabs>
          </VStack>
      </ShowcaseDemoSection>

      {/* With Icons */}
      <ShowcaseDemoSection
        title="With Icons"
        description="Tab labels can include icons for better visual recognition"
       
      >
          <Tabs defaultValue="dashboard">
            <Tabs.List>
              <Tabs.Tab value="dashboard">
                <HStack gap={2} align="center">
                  <Icon icon={LayoutDashboard} size="sm" />
                  <span>Dashboard</span>
                </HStack>
              </Tabs.Tab>
              <Tabs.Tab value="policies">
                <HStack gap={2} align="center">
                  <Icon icon={Shield} size="sm" />
                  <span>Policies</span>
                </HStack>
              </Tabs.Tab>
              <Tabs.Tab value="claims">
                <HStack gap={2} align="center">
                  <Icon icon={ClipboardList} size="sm" />
                  <span>Claims</span>
                </HStack>
              </Tabs.Tab>
              <Tabs.Tab value="customers">
                <HStack gap={2} align="center">
                  <Icon icon={Users} size="sm" />
                  <span>Customers</span>
                </HStack>
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="dashboard">
              <Text>Main dashboard with key performance indicators and alerts.</Text>
            </Tabs.Panel>
            <Tabs.Panel value="policies">
              <Text>Manage all insurance policies and coverage details.</Text>
            </Tabs.Panel>
            <Tabs.Panel value="claims">
              <Text>Process and track insurance claims efficiently.</Text>
            </Tabs.Panel>
            <Tabs.Panel value="customers">
              <Text>Customer relationship management and contact information.</Text>
            </Tabs.Panel>
          </Tabs>
      </ShowcaseDemoSection>

      {/* Disabled Tabs */}
      <ShowcaseDemoSection
        title="Disabled Tabs"
        description="Individual tabs can be disabled to prevent interaction"
       
      >
          <Tabs defaultValue="active">
            <Tabs.List>
              <Tabs.Tab value="active">Active Policies</Tabs.Tab>
              <Tabs.Tab value="pending" disabled>Pending Review</Tabs.Tab>
              <Tabs.Tab value="expired">Expired Policies</Tabs.Tab>
              <Tabs.Tab value="cancelled" disabled>Cancelled</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="active">
              <Text>View all currently active insurance policies.</Text>
            </Tabs.Panel>
            <Tabs.Panel value="pending">
              <Text>Policies pending review (access restricted).</Text>
            </Tabs.Panel>
            <Tabs.Panel value="expired">
              <Text>View expired policies and renewal options.</Text>
            </Tabs.Panel>
            <Tabs.Panel value="cancelled">
              <Text>Cancelled policies archive (access restricted).</Text>
            </Tabs.Panel>
          </Tabs>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const TabsShowcase: ShowcaseDefinition = {
  id: 'tabs',
  name: 'Tabs',
  category: 'navigation',
  description: 'Tabbed interface for organizing content into sections',
  propsDefinition: [
    {
      name: 'variant',
      type: 'select',
      options: ['underline', 'contained'],
      defaultValue: 'underline',
      description: 'Visual style of the tabs',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="tablist" on tab container',
      'role="tab" on each tab button',
      'role="tabpanel" on content panels',
      'aria-selected indicates active tab',
      'aria-controls links tab to its panel',
    ],
    keyboardNav: [
      'Arrow Left/Right: Navigate between tabs',
      'Home: Jump to first tab',
      'End: Jump to last tab',
      'Enter/Space: Activate focused tab (implicit)',
    ],
    notes: [
      'Tab panels receive focus via tabIndex={0}',
      'Keyboard navigation wraps around at ends',
      'Disabled tabs are skipped in keyboard navigation',
    ],
  },
  component: TabsShowcaseComponent,
};
